import React from 'react'
import { StyledModal, StyledModalButton } from '@/components/ui/styled-modal'
import { Input } from '@/components/ui/input'
import { useSmartListsStore } from '@/store/smartListsStore'
import { useAuthStore } from '@/store/authStore'
import { useTransactionStore } from '@/store/transactionsStore'
import { useCategoriesStore } from '@/store/categoriesStore'
import { usePayeesStore } from '@/store/payeesStore'
import { useState, useEffect } from 'react'
import { SmartList, SmartListFilter } from '@/types/smartList'
import { TransactionType } from '@/types/transaction'
import { Badge } from '@/components/ui/badge'
import { Combobox } from '@headlessui/react'
import { Fragment } from 'react'
import { Transition } from '@headlessui/react'
import { cn } from '@/lib/utils'
import * as LucideIcons from 'lucide-react'
import { 
  Filter, 
  AlertCircle, 
  Trash2, 
  ChevronDown, 
  Search, 
  Calendar,
  DollarSign,
  Tag,
  User,
  Check,
  X,
  Eye
} from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"



interface EditSmartListModalProps {
  smartList: SmartList
  isOpen: boolean
  onClose: () => void
}

function getFilterSummary(filters: SmartListFilter) {
  const summary = []

  if (filters.dateRange) 
    summary.push(`Date: ${filters.dateRange}`)
  
  if (filters.type && filters.type !== 'all') 
    summary.push(`Type: ${filters.type}`)
  
  if (filters.accounts && filters.accounts.length > 0) 
    summary.push('Account selected')
  
  if (filters.search) 
    summary.push(`Search: ${filters.search}`)
  
  if (filters.tags && filters.tags.length > 0) 
    summary.push(`Tags: ${filters.tags.length} selected`)

  return summary
}



// Enhanced SmartListIconPicker component
function SmartListIconPicker({ value, onChange }: { value: string; onChange: (icon: string) => void }) {
  const iconOptions = [
    'Filter', 'List', 'BarChart2', 'PieChart', 'TrendingUp', 'DollarSign', 'Calendar', 'Tag',
    'CreditCard', 'PiggyBank', 'Receipt', 'Wallet', 'Target', 'Zap', 'Star', 'Heart',
    'Home', 'Car', 'Plane', 'ShoppingBag', 'Coffee', 'Utensils', 'Gamepad2', 'Music',
    'Book', 'Briefcase', 'GraduationCap', 'Users', 'Phone', 'Mail', 'MapPin', 'Camera'
  ]

  return (
    <div className="grid grid-cols-8 gap-2">
      {iconOptions.map(iconName => {
        const IconComponent = (LucideIcons as any)[iconName]
        if (!IconComponent) return null
        return (
          <button
            key={iconName}
            type="button"
            onClick={() => onChange(iconName)}
            className={cn(
              "w-10 h-10 rounded-lg flex items-center justify-center transition-colors",
              value === iconName
                ? "bg-[#439476] text-white"
                : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
            )}
          >
            <IconComponent className="w-5 h-5" />
          </button>
        )
      })}
    </div>
  )
}

export function EditSmartListModal({ smartList, isOpen, onClose }: EditSmartListModalProps) {
  const { user } = useAuthStore()
  const { updateSmartList, deleteSmartList } = useSmartListsStore()
  const { filters, setFilters, resetFilters } = useTransactionStore() as any
  const { categories } = useCategoriesStore()
  const { payees } = usePayeesStore()
  const [name, setName] = useState(smartList.name)
  const [icon, setIcon] = useState(smartList.icon || '')
  const [color, setColor] = useState(smartList.color || '#6366F1')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [currentFilters, setCurrentFilters] = useState<SmartListFilter>(smartList.filters)
  
  // Combobox states
  const [payeeQuery, setPayeeQuery] = useState('')
  const [categoryQuery, setCategoryQuery] = useState('')
  const [isPayeeOpen, setIsPayeeOpen] = useState(false)
  const [isCategoryOpen, setIsCategoryOpen] = useState(false)

  // Handle clicking outside to close comboboxes
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.combobox-container')) {
        setIsCategoryOpen(false);
        setIsPayeeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [])

  const originalFilterSummary = getFilterSummary(smartList.filters)
  const currentFilterSummary = getFilterSummary(currentFilters)
  const hasFilterChanges = JSON.stringify(originalFilterSummary) !== JSON.stringify(currentFilterSummary)

  useEffect(() => {
    if (isOpen) {
      setFilters({
        dateRange: smartList.filters.dateRange as any,
        type: smartList.filters.type === 'all' ? undefined : smartList.filters.type as TransactionType,
        accounts: smartList.filters.accounts,
        search: smartList.filters.search,
        tags: smartList.filters.tags || []
      })
      setName(smartList.name)
      setIcon(smartList.icon || '')
      setColor(smartList.color || '#6366F1')
    }
  }, [smartList, isOpen, setFilters])

  const handleClose = () => {
    resetFilters()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user?.id) return

    setIsSubmitting(true)
    try {
      // Get the current type value, ensuring it's a single type or 'all'
      let currentType: 'all' | TransactionType = 'all'
      if (filters.type) {
        if (Array.isArray(filters.type)) {
          if (filters.type.length > 0) {
            currentType = filters.type[0]
          }
        } else {
          currentType = filters.type
        }
      }

      const updatedSmartList: SmartList = {
        ...smartList,
        name,
        icon,
        color,
        filters: currentFilters
      }

      await updateSmartList(smartList.id, updatedSmartList)
      handleClose()
    } catch (error) {
      console.error('Error updating smart list:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!user?.id) return

    try {
      await deleteSmartList(smartList.id)
      handleClose()
    } catch (error) {
      console.error('Error deleting smart list:', error)
    }
  }

  return (
    <>
      <StyledModal
        isOpen={isOpen}
        onClose={handleClose}
        title="Edit Smart List"
        maxWidth="600px"
      >

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-[#2F2F2F] dark:text-[#A3A3A3]">
                Name
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter smart list name"
                required
                className="
                  h-11 w-full rounded-xl 
                  border border-[#2F2F2F]/10 
                  bg-white/50 dark:bg-[#1E2B42]/50 
                  px-3 py-2 
                  text-[#1C1C1C] dark:text-[#E8E8E8] 
                  shadow-sm 
                  focus:ring-2 focus:ring-[#439476] 
                  dark:border-[#333840] 
                  transition-colors duration-200
                "
              />
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium text-[#2F2F2F] dark:text-[#A3A3A3]">
                <Filter className="w-4 h-4 inline mr-2" />
                Edit Filters
              </label>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-xl bg-white/50 dark:bg-[#1E2B42]/50 border border-[#2F2F2F]/10 dark:border-[#333840]">
                {/* Transaction Type */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <DollarSign className="w-4 h-4 inline mr-2" />
                    Transaction Type
                  </label>
                  <select
                                         value={currentFilters.type || 'all'}
                     onChange={(e) => {
                       const updatedFilters = {
                         ...currentFilters,
                         type: e.target.value === 'all' ? undefined : e.target.value as any
                       };
                       setCurrentFilters(updatedFilters);
                     }}
                    className="w-full h-11 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-2"
                  >
                    <option value="all">All Types</option>
                    <option value="income">Income</option>
                    <option value="expense">Expense</option>
                    <option value="transfer">Transfer</option>
                  </select>
                </div>

                {/* Date Range */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Date Range
                  </label>
                  <select
                                         value={currentFilters.dateRange || 'all'}
                     onChange={(e) => {
                       const updatedFilters = {
                         ...currentFilters,
                         dateRange: e.target.value === 'all' ? undefined : e.target.value as any
                       };
                       setCurrentFilters(updatedFilters);
                     }}
                    className="w-full h-11 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-2"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="thisWeek">This Week</option>
                    <option value="thisMonth">This Month</option>
                    <option value="lastMonth">Last Month</option>
                    <option value="thisYear">This Year</option>
                  </select>
                </div>

                {/* Amount Range */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Min Amount
                  </label>
                  <Input
                    type="number"
                                         value={currentFilters.minAmount || ''}
                     onChange={(e) => {
                       const updatedFilters = {
                         ...currentFilters,
                         minAmount: e.target.value ? Number(e.target.value) : undefined
                       };
                       setCurrentFilters(updatedFilters);
                     }}
                    placeholder="0.00"
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Max Amount
                  </label>
                  <Input
                    type="number"
                                         value={currentFilters.maxAmount || ''}
                     onChange={(e) => {
                       const updatedFilters = {
                         ...currentFilters,
                         maxAmount: e.target.value ? Number(e.target.value) : undefined
                       };
                       setCurrentFilters(updatedFilters);
                     }}
                    placeholder="1000.00"
                    className="h-11"
                  />
                </div>

                {/* Categories */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Tag className="w-4 h-4 inline mr-2" />
                    Categories
                  </label>
                  <Combobox
                    as="div"
                                         value={currentFilters.category || 'all'}
                     onChange={(value) => {
                       const updatedFilters = {
                         ...currentFilters,
                         category: value === 'all' ? undefined : value
                       };
                       setCurrentFilters(updatedFilters);
                       setIsCategoryOpen(false);
                     }}
                    className="relative combobox-container"
                  >
                    <div className="relative">
                      <div
                        className="flex items-center h-11 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl px-4 cursor-pointer hover:ring-2 hover:ring-[#439476]/30 hover:border-[#439476] transition-all duration-200"
                        onClick={() => setIsCategoryOpen(true)}
                      >
                        <Tag className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <Combobox.Input
                          className="bg-transparent flex-1 outline-none border-none text-gray-900 dark:text-white placeholder:text-gray-500"
                          placeholder="Search categories..."
                          onChange={(e) => setCategoryQuery(e.target.value)}
                          displayValue={(categoryId: string) => {
                            if (!categoryId || categoryId === 'all') return '';
                            if (categoryId === 'none') return 'No Category';
                            
                            const category = categories.find(c => c.id === categoryId);
                            return category ? category.name : '';
                          }}
                        />
                        {categoryQuery && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCategoryQuery('');
                            }}
                            className="text-gray-400 hover:text-gray-600 transition-colors mr-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        <ChevronDown className="w-4 h-4 text-gray-400 ml-2 flex-shrink-0" />
                      </div>
                    </div>

                    <Transition
                      show={isCategoryOpen}
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Combobox.Options
                        static
                        className="absolute z-50 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl mt-1 py-2 text-base shadow-lg max-h-60 overflow-auto focus:outline-none"
                      >
                        <Combobox.Option
                          value="none"
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 px-4 ${
                              active ? 'bg-[#439476]/10 text-[#439476]' : 'text-gray-900 dark:text-white'
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                No Category
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#439476]">
                                  <Check className="h-4 w-4" />
                                </span>
                              )}
                            </>
                          )}
                        </Combobox.Option>
                        <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                        {categories
                          .filter(category => 
                            categoryQuery === '' || 
                            category.name.toLowerCase().includes(categoryQuery.toLowerCase())
                          )
                          .map((category) => (
                            <Combobox.Option
                              key={category.id}
                              value={category.id}
                              className={({ active }) =>
                                `relative cursor-pointer select-none py-2 px-4 ${
                                  active ? 'bg-[#439476]/10 text-[#439476]' : 'text-gray-900 dark:text-white'
                                }`
                              }
                            >
                              {({ selected }) => (
                                <>
                                  <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                    {category.name}
                                  </span>
                                  {selected && (
                                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#439476]">
                                      <Check className="h-4 w-4" />
                                    </span>
                                  )}
                                </>
                              )}
                            </Combobox.Option>
                        ))}
                      </Combobox.Options>
                    </Transition>
                  </Combobox>
                </div>

                {/* Payees */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <User className="w-4 h-4 inline mr-2" />
                    Payees
                  </label>
                  <Combobox
                    as="div"
                                         value={currentFilters.payee || 'all'}
                     onChange={(value) => {
                       const updatedFilters = {
                         ...currentFilters,
                         payee: value === 'all' ? undefined : value
                       };
                       setCurrentFilters(updatedFilters);
                       setIsPayeeOpen(false);
                     }}
                    className="relative combobox-container"
                  >
                    <div className="relative">
                      <div
                        className="flex items-center h-11 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl px-4 cursor-pointer hover:ring-2 hover:ring-[#439476]/30 hover:border-[#439476] transition-all duration-200"
                        onClick={() => setIsPayeeOpen(true)}
                      >
                        <User className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <Combobox.Input
                          className="bg-transparent flex-1 outline-none border-none text-gray-900 dark:text-white placeholder:text-gray-500"
                          placeholder="Search payees..."
                          onChange={(e) => setPayeeQuery(e.target.value)}
                          displayValue={(payeeId: string) => {
                            if (!payeeId || payeeId === 'all') return '';
                            if (payeeId === 'none') return 'No Payee';
                            
                            const payee = payees.find(p => p.id === payeeId);
                            return payee ? payee.name : '';
                          }}
                        />
                        {payeeQuery && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPayeeQuery('');
                            }}
                            className="text-gray-400 hover:text-gray-600 transition-colors mr-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        <ChevronDown className="w-4 h-4 text-gray-400 ml-2 flex-shrink-0" />
                      </div>
                    </div>

                    <Transition
                      show={isPayeeOpen}
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Combobox.Options
                        static
                        className="absolute z-50 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl mt-1 py-2 text-base shadow-lg max-h-60 overflow-auto focus:outline-none"
                      >
                        <Combobox.Option
                          value="none"
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 px-4 ${
                              active ? 'bg-[#439476]/10 text-[#439476]' : 'text-gray-900 dark:text-white'
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                No Payee
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#439476]">
                                  <Check className="h-4 w-4" />
                                </span>
                              )}
                            </>
                          )}
                        </Combobox.Option>
                        <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                        {payees
                          .filter(payee => 
                            payeeQuery === '' || 
                            payee.name.toLowerCase().includes(payeeQuery.toLowerCase())
                          )
                          .map((payee) => (
                            <Combobox.Option
                              key={payee.id}
                              value={payee.id}
                              className={({ active }) =>
                                `relative cursor-pointer select-none py-2 px-4 ${
                                  active ? 'bg-[#439476]/10 text-[#439476]' : 'text-gray-900 dark:text-white'
                                }`
                              }
                            >
                              {({ selected }) => (
                                <>
                                  <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                    {payee.name}
                                  </span>
                                  {selected && (
                                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#439476]">
                                      <Check className="h-4 w-4" />
                                    </span>
                                  )}
                                </>
                              )}
                            </Combobox.Option>
                        ))}
                      </Combobox.Options>
                    </Transition>
                  </Combobox>
                </div>

                {/* Search */}
                <div className="space-y-2 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Search className="w-4 h-4 inline mr-2" />
                    Search Term
                  </label>
                  <Input
                                         value={currentFilters.search || ''}
                     onChange={(e) => {
                       const updatedFilters = {
                         ...currentFilters,
                         search: e.target.value || undefined
                       };
                       setCurrentFilters(updatedFilters);
                     }}
                    placeholder="Search in descriptions, notes, payees..."
                    className="h-11"
                  />
                </div>
              </div>

              {hasFilterChanges && (
                <>
                  <div className="
                    flex items-center gap-2 p-3 rounded-xl
                    text-[#439476] dark:text-[#439476]
                    bg-[#439476]/10 dark:bg-[#439476]/10
                    border border-[#439476]/20 dark:border-[#439476]/20
                    transition-colors duration-200
                  ">
                    <AlertCircle className="h-4 w-4" />
                    <span>The filters have been modified. Saving will update this smart list with the new filters:</span>
                  </div>
                  
                  <div className="
                    p-4 rounded-xl
                    bg-white/50 dark:bg-[#1E2B42]/50
                    border border-[#2F2F2F]/10 dark:border-[#333840]
                    space-y-3
                    transition-colors duration-200
                  ">
                    <div className="flex items-center gap-2 text-sm text-[#2F2F2F] dark:text-[#A3A3A3]">
                      <Filter className="w-4 h-4" />
                      <span>New filters to be saved:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {currentFilterSummary.length > 0 ? (
                        currentFilterSummary.map((filter, index) => (
                          <Badge 
                            key={index} 
                            variant="secondary"
                            className="
                              bg-white/80 dark:bg-[#182438]/80 
                              text-[#1C1C1C] dark:text-[#E8E8E8] 
                              border border-[#2F2F2F]/10 dark:border-[#333840]
                              backdrop-blur-xl backdrop-saturate-150
                              transition-colors duration-200
                            "
                          >
                            {filter}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-sm text-[#2F2F2F] dark:text-[#A3A3A3]">No filters will be applied</span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="space-y-6">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#2F2F2F] dark:text-[#A3A3A3]">
                  Icon
                </label>
                <SmartListIconPicker 
                  value={icon} 
                  onChange={setIcon}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#2F2F2F] dark:text-[#A3A3A3]">
                  Color
                </label>
                <div className="space-y-2">
                  <div className="grid grid-cols-6 gap-2">
                    {[
                      '#439476', '#6366F1', '#8B5CF6', '#EC4899', '#EF4444', '#F59E0B',
                      '#10B981', '#06B6D4', '#84CC16', '#F97316', '#EAB308', '#6B7280'
                    ].map(colorOption => (
                      <button
                        key={colorOption}
                        onClick={() => setColor(colorOption)}
                        className={cn(
                          "w-8 h-8 rounded-lg border-2 transition-all",
                          color === colorOption
                            ? "border-gray-400 scale-110"
                            : "border-gray-200 dark:border-gray-600 hover:scale-105"
                        )}
                        style={{ backgroundColor: colorOption }}
                      />
                    ))}
                  </div>
                  <Input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="w-full h-8"
                  />
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#2F2F2F] dark:text-[#A3A3A3]">
                <Eye className="w-4 h-4 inline mr-2" />
                Preview
              </label>
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                <div className="flex items-center gap-3 p-3 bg-white dark:bg-gray-700 rounded-lg">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: color + '20' }}
                  >
                    {(() => {
                      const IconComponent = (LucideIcons as any)[icon] || Filter
                      return <IconComponent className="w-4 h-4" style={{ color: color }} />
                    })()}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {name || 'Smart List Name'}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {getFilterSummary(currentFilters).length > 0 
                        ? getFilterSummary(currentFilters).join(' • ')
                        : 'No filters applied'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-between items-center pt-4">
              <StyledModalButton
                type="button"
                variant="danger"
                onClick={() => setShowDeleteDialog(true)}
                icon={<Trash2 className="w-4 h-4" />}
              >
                Delete List
              </StyledModalButton>
              
              <div className="flex gap-3">
                <StyledModalButton 
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                >
                  Cancel
                </StyledModalButton>
                <StyledModalButton 
                  type="submit" 
                  variant="success"
                  disabled={isSubmitting}
                >
                  Update Smart List
                </StyledModalButton>
              </div>
            </div>
          </form>
        </StyledModal>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent 
          className="
            bg-white/80 dark:bg-[#182438]/80 
            backdrop-blur-xl backdrop-saturate-150 
            border border-[#2F2F2F]/10 dark:border-[#333840] 
            shadow-sm
            rounded-xl
          "
          style={{ zIndex: 99999 }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="
              text-xl font-semibold 
              bg-gradient-to-r from-[#1C1C1C] to-[#383838] 
              dark:from-[#E8E8E8] dark:to-[#CCCCCC] 
              bg-clip-text text-transparent
            ">
              Delete Smart List
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#2F2F2F] dark:text-[#A3A3A3]">
              Are you sure you want to delete "{smartList.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="
              h-11 px-4
              bg-white/80 dark:bg-[#182438]/80 
              text-[#1C1C1C] dark:text-[#E8E8E8] 
              hover:bg-white/90 dark:hover:bg-[#182438]/90 
              border border-[#2F2F2F]/10 dark:border-[#333840] 
              backdrop-blur-xl backdrop-saturate-150
              rounded-xl
              transition-colors duration-200
            ">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="
                h-11 px-4
                bg-[#E53935] hover:bg-[#E53935]/90
                text-white
                rounded-xl
                transition-colors duration-200
              "
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
} 